import { EventType } from "@/analytics/analytics-events";
import {
  ActivateSheetProperties,
  IsolateSheetProperties,
} from "@/analytics/multi-sheet-management-events-properties";
import { openAlignmentWizard } from "@/modes/alignment-wizard/open-alignment-wizard";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  convertToDateString,
  cyan,
  ExclamationMarkCircleFillIcon,
  FaroButton,
  FaroIconButton,
  FaroTooltip,
  IconBadgeData,
  LayoutSheetSmallIcon,
  LocationMapIcon,
  NoTranslate,
  SingleSelectItem,
  SingleSelectItemProps,
  TranslateVar,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElementAreaSection,
  IElementGenericDataSession,
  IElementGenericDataset,
  IElementGenericImgSheet,
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementGenericDataSession,
  isIElementGenericDataset,
  isIElementGenericImgSheet,
  isIElementOverviewImage,
  isIElementWithTypeAndHint,
} from "@faro-lotv/ielement-types";
import {
  selectAncestor,
  selectChildrenDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import { Stack, SvgIconProps } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { MouseEvent, useCallback, useMemo, useState, type JSX } from "react";
import { ElementIcon, ElementIconType } from "../icons";
import { isSheetAligned } from "../tree/tree-context-menu/actions/mark-sheet-as-aligned";
import { ContextMenu } from "../tree/tree-context-menu/tree-context-menu";

export type MultiSheetMenuItemProps = Pick<
  SingleSelectItemProps,
  "selectedValue"
> & {
  /** The sheet element for this item */
  sheet: IElementGenericImgSheet;

  /** Number of visible sheets in multi-sheet menu for selected area  */
  visibleSheetsInArea: number;

  /** Flag specifying if the sheet is currently visible */
  isVisible: boolean;

  /** Total number of sheets in multi-sheet menu for selected area  */
  sheetsInArea: number;

  /** The list of sheets marked as visible by the user */
  listVisibleSheets: GUID[];

  /** The callback executed when an element is clicked */
  onElementClicked(id: GUID): void;

  /** The callback executed when the eye icon is clicked */
  onEyeClicked(id: GUID, isVisible: boolean): void;

  /**
   * True if the user can open the editing options (add, align, etc...) from the menu
   *
   * @default true
   */
  showEditOptions?: boolean;
};

/** @returns the select item for each available sheet */
export function MultiSheetMenuItem({
  sheet,
  sheetsInArea,
  visibleSheetsInArea,
  listVisibleSheets,
  onElementClicked,
  onEyeClicked,
  isVisible,
  showEditOptions = true,
  ...rest
}: MultiSheetMenuItemProps): JSX.Element {
  const dispatch = useAppDispatch();
  const reference = useAppSelector((state) =>
    selectSheetReference(state, sheet),
  );

  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const isAligned = useAppSelector(selectIsSheetAligned(sheet));

  const startAlignmentWizard = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // we do not want the SingleSelectItem's onClick to react to this click
      event.stopPropagation();

      Analytics.track(EventType.pressMultiSheetItemAlignButton);

      openAlignmentWizard({
        elementIdToAlign: sheet.id,
        dispatch,
      });
    },
    [dispatch, sheet.id],
  );

  const [displayTooltip, setDisplayTooltip] = useState(false);

  const unalignedIconBadgeData: IconBadgeData = useMemo(
    () => ({
      badgeContent: (
        <ExclamationMarkCircleFillIcon
          sx={{ fontSize: "1em", color: "warning.main" }}
        />
      ),
      badgeBackgroundColor: "default",
    }),
    [],
  );

  return (
    <SingleSelectItem
      {...rest}
      selected={false}
      selectedValue={undefined}
      disabled={false}
      onClick={() => {
        Analytics.track<IsolateSheetProperties>(EventType.isolateSheet, {
          sheetType: isIElementOverviewImage(sheet)
            ? "overviewMap"
            : "floorPlan",
          previousVisibleSheets: listVisibleSheets.length,
        });

        onElementClicked(sheet.id);
      }}
      Icon={getSheetIconComponent(reference)}
      iconBadgeData={isAligned ? undefined : unalignedIconBadgeData}
      label={
        <TruncatedFaroText variant="bodyM" color="inherit" dark>
          <NoTranslate component="span">{sheet.name}</NoTranslate>
        </TruncatedFaroText>
      }
      extraAction={
        <Stack direction="row" sx={{ alignItems: "center" }}>
          {!isAligned && hasWritePermission && (
            <FaroButton
              aria-label="align-sheet-button"
              variant="ghost"
              size="xs"
              dark
              onClick={startAlignmentWizard}
              sx={{
                minWidth: 0,
              }}
            >
              Align
            </FaroButton>
          )}
          <FaroTooltip
            title="At least one sheet must remain visible"
            open={displayTooltip}
            onClose={() => setDisplayTooltip(false)}
          >
            <EyeButton
              isVisible={isVisible}
              onClick={(event) => {
                // we do not want the SingleSelectItem's onClick to react to this click
                event.stopPropagation();

                if (visibleSheetsInArea > 1 || !isVisible) {
                  Analytics.track<ActivateSheetProperties>(
                    isVisible
                      ? EventType.deactivateSheet
                      : EventType.activateSheet,
                    {
                      sheetType: isIElementOverviewImage(sheet)
                        ? "overviewMap"
                        : "floorPlan",
                      previousVisibleSheets: visibleSheetsInArea,
                      totalSheets: sheetsInArea,
                    },
                  );

                  onEyeClicked(sheet.id, !isVisible);
                  setDisplayTooltip(false);
                } else {
                  setDisplayTooltip(true);
                }
              }}
            />
          </FaroTooltip>
          {hasWritePermission && showEditOptions && (
            <ContextMenu
              id={sheet.id}
              isNodeDisabled={false}
              dark
              iconHoverColor={hoverColorForIcons}
            />
          )}
        </Stack>
      }
      key={sheet.id}
      value={sheet.id}
      secondaryText={
        reference ? (
          <MultiSheetMenuItemSecondaryText reference={reference} />
        ) : undefined
      }
      dark
    />
  );
}

type MultiSheetMenuItemSecondaryTextProps = {
  /** The reference element the current sheet is part of */
  reference:
    | IElementGenericDataSession
    | IElementGenericDataset
    | IElementAreaSection;
};

/** @returns the secondary text for a sheet menu select item */
function MultiSheetMenuItemSecondaryText({
  reference,
}: MultiSheetMenuItemSecondaryTextProps): JSX.Element | null {
  return useMemo(() => {
    if (isIElementAreaSection(reference)) {
      return null;
    }
    return (
      <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
        <TranslateVar name="sheetName">
          {convertToDateString(reference.createdAt)} {reference.name}
        </TranslateVar>
      </Stack>
    );
  }, [reference]);
}

/**
 * @returns the icon component for a sheet
 * @param reference the reference element for a sheet (area or dataset)
 */
export function getSheetIconComponent(
  reference?:
    | IElementGenericDataSession
    | IElementGenericDataset
    | IElementAreaSection,
): ((props: SvgIconProps) => JSX.Element) | undefined {
  if (!reference) return;

  if (isIElementAreaSection(reference)) {
    return LayoutSheetSmallIcon;
  } else if (
    isIElementGenericDataSession(reference) ||
    isIElementGenericDataset(reference)
  ) {
    return LocationMapIcon;
  }
}

/**
 * @param state the app state
 * @param sheet the sheet to compute the reference for
 * @returns the reference element for a sheet (data session or data set)
 */
const selectSheetReference = createSelector(
  [
    (state: RootState, sheet: IElementGenericImgSheet) =>
      selectAncestor(sheet, isIElementAreaSection)(state),
    (state: RootState, sheet: IElementGenericImgSheet) =>
      selectAncestor(sheet, isIElementGenericDataSession)(state),
    (state: RootState, sheet: IElementGenericImgSheet) =>
      selectAncestor(sheet, isIElementGenericDataset)(state),
  ],
  (area, dataSession, dataSet) => dataSession ?? dataSet ?? area,
);

type EyeButtonProps = {
  /** Method to call when an item is selected */
  onClick(event: MouseEvent<HTMLButtonElement>): void;

  /** Flag used to show correct eye icon in menu: Visible/NonVisible  */
  isVisible: boolean;
};

/** @returns render eye button */
function EyeButton({ isVisible, onClick }: EyeButtonProps): JSX.Element {
  return (
    <FaroIconButton
      onClick={onClick}
      margin={0.5}
      size="s"
      aria-label="toggle sheet visibility"
      // Get both the image color and its hover color from the container
      color="inherit"
      hoverColor={hoverColorForIcons}
      sx={{
        color: "inherit",
      }}
    >
      <ElementIcon
        icon={isVisible ? ElementIconType.Visible : ElementIconType.NonVisible}
      />
    </FaroIconButton>
  );
}

// Color for mouse hover on the icon buttons
// Should be the same as FaroButton's FaroButtonVariants.ghost text color
const hoverColorForIcons = cyan[400];

const selectIsSheetAligned = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, sheet: IElementGenericImgSheet) => sheet,
    ],
    (iElements, sheet): boolean => {
      if (isSheetAligned(sheet)) {
        return true;
      }
      // There is a special case in the alignment logic, when an area contains exactly one sheet.
      // In that case, the area will be aligned instead of the sheet, keeping the sheet technically without pose.
      // To reflect that in the UI, we consider the sheet as aligned, and the user will see a warning
      // that the area needs to be aligned instead. We must count only sheet descendants (not rooms,
      // panoramas, or other groups), consistent with selectShouldShowMarkAsAlignedAction.
      const parentElement = selectIElement(sheet.parentId)({ iElements });
      if (
        !parentElement ||
        !isIElementWithTypeAndHint(
          parentElement,
          IElementType.group,
          IElementTypeHint.area,
        )
      ) {
        return false;
      }
      const sheets = selectChildrenDepthFirst(
        parentElement,
        isIElementGenericImgSheet,
        undefined,
        (found) => found.length > 1,
      )({ iElements });
      return sheets.length === 1;
    },
  ),
);
